import React, { useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Tooltip } from '@material-ui/core';

import { loadMergeSyncHealth } from './actions/load-merge-sync-health';
import { mergeSyncHealthForOrgIdSelector } from './merge-integration-selectors';
import { openOrCloseGlobalDialog } from '../action/dialog/global-dialog';
import { loadHrisIntegrationResponse } from '../hris-integration-response/actions/load-hris-integration-response';
import { hrisIntegrationResponseForOrgIdSelector } from '../hris-integration-response/hris-integration-response-selectors';
import { userSelector } from '../reducers/current-user-selectors';
import { orgsForAccountId } from '../reducers/org-selectors';

import { GLOBAL_DIALOGS } from '../dialogs/global-dialogs-config';
import { employeesRoute } from '../web-routes';

import {
  MERGE_SYNC_HEALTH_STATUS_TYPE,
  MergeSyncHealthStatusType,
} from './util/merge-sync-health-types';
import { MergeLinkButton } from './widgets/merge-link-button';
import { HelpScout } from '../util/helpscout-beacon';
import {
  FilledSmallCircleIcon,
  ICON_NAMES,
  MaterialDesignIcon,
} from '../util/material-design-icons';

type Props = {
  orgId: number;
  mergeOriginOrgId: number;
  accountId: number;
  showSyncHealthDescription?: boolean;
  showTooltip?: boolean;
} & React.PropsWithChildren<{}>;

const MERGE_SYNC_HEALTH_STATUS_MAP = {
  GOOD: {
    colorClass: 'u-color-green',
    header: 'Successful',
    defaultDescription: 'Your integration appears to be healthy.',
  },
  DELAYED: {
    colorClass: 'u-color-yellow',
    header: 'Delayed',
    defaultDescription: 'There is an issue that has delayed syncing with your HRIS system.',
  },
  REAUTHENTICATION_NEEDED: {
    colorClass: 'u-color-red',
    header: 'Reauthentication Needed',
    defaultDescription: 'Your account needs to be reauthenticated.',
  },
  PAUSED: { colorClass: 'u-color-gray', header: 'Import Paused', defaultDescription: null },
  SYNCING: {
    colorClass: null,
    header: 'Import in Progress',
    defaultDescription: 'An import is in progress.',
  },
};

export const MergeIntegrationSyncHealthIndicator = ({
  orgId,
  mergeOriginOrgId,
  accountId,
  showSyncHealthDescription,
  showTooltip,
  children,
}: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const mergeSyncHealth = useSelector((state: any) =>
    mergeSyncHealthForOrgIdSelector(state, orgId),
  );

  const hrisIntegrationResponse = useSelector((state: any) =>
    hrisIntegrationResponseForOrgIdSelector(state, orgId),
  );

  const user = useSelector((state: any) => userSelector(state));
  const orgsUserCanAccess = useSelector((state: any) => orgsForAccountId(state, accountId));
  useEffect(() => {
    dispatch(loadMergeSyncHealth(orgId));
    dispatch(loadHrisIntegrationResponse(orgId));
  }, [orgId, dispatch]);

  const status: MergeSyncHealthStatusType | null = mergeSyncHealth?.status;
  const description = mergeSyncHealth?.description;

  const isMosSync = orgId !== mergeOriginOrgId;
  const userIsAdmin = user.isAccountAdmin || user.isAdmin;
  const userHasAccessToMosOrg = orgsUserCanAccess?.find((org) => org.org_id === mergeOriginOrgId);
  const actionButton =
    !isMosSync &&
    userIsAdmin &&
    status === MERGE_SYNC_HEALTH_STATUS_TYPE.REAUTHENTICATION_NEEDED ? (
      <div className="u-margin-top-sm">
        <MergeLinkButton
          mergeIntegrationKey={hrisIntegrationResponse?.HrisIntegrationType?.mergeIntegrationKey}
          isIntegrated
          orgId={mergeOriginOrgId}
          size="sm"
          variant="secondary"
        />
      </div>
    ) : null;

  const handleMergeOrgClick = () => {
    dispatch(openOrCloseGlobalDialog(GLOBAL_DIALOGS.MERGE_INTEGRATION_DETAILS_DIALOG.key, false));
    history.push(employeesRoute(mergeOriginOrgId));
  };

  const shortDescription =
    description || (status ? MERGE_SYNC_HEALTH_STATUS_MAP[status].defaultDescription : null);

  const visitOrg = (
    <>
      Your HRIS integration is managed through a&nbsp;
      <span className="typo-link" onClick={handleMergeOrgClick} role="presentation">
        different organization
      </span>
      &nbsp; under your account.&nbsp;
    </>
  );

  const descriptionToDisplay = (
    <>
      {shortDescription}{' '}
      {isMosSync && userIsAdmin && userHasAccessToMosOrg
        ? visitOrg
        : (status === MERGE_SYNC_HEALTH_STATUS_TYPE.DELAYED ||
            status === MERGE_SYNC_HEALTH_STATUS_TYPE.REAUTHENTICATION_NEEDED) &&
          isMosSync &&
          !userIsAdmin
        ? 'Your account admin has been notified.'
        : ''}
    </>
  );

  const contactSupportStatement = (
    <>
      {status === MERGE_SYNC_HEALTH_STATUS_TYPE.REAUTHENTICATION_NEEDED
        ? 'If you need help with this process'
        : 'If you have questions'}
      , please{' '}
      <span
        className="typo-link"
        role="presentation"
        onClick={() => {
          HelpScout.beacon.navigate('/ask/message');
          HelpScout.beacon.prefillText('HRIS Syncing Support');
        }}
      >
        contact support
      </span>
      .
    </>
  );

  const generateIcon = () => {
    const colorClass = status ? MERGE_SYNC_HEALTH_STATUS_MAP[status].colorClass : null;

    const icon =
      status === MERGE_SYNC_HEALTH_STATUS_TYPE.SYNCING ? (
        <MaterialDesignIcon iconName={ICON_NAMES.CACHED} color="gray" />
      ) : colorClass ? (
        <FilledSmallCircleIcon className={colorClass} />
      ) : null;

    const shouldShowTooltip =
      showTooltip &&
      (status === MERGE_SYNC_HEALTH_STATUS_TYPE.DELAYED ||
        status === MERGE_SYNC_HEALTH_STATUS_TYPE.REAUTHENTICATION_NEEDED);
    if (shouldShowTooltip && icon) {
      return (
        <Tooltip
          title={`Your integration ${
            status === MERGE_SYNC_HEALTH_STATUS_TYPE.DELAYED ? 'is delayed' : 'needs your attention'
          }. Open the dialog for more details`}
        >
          <span>{icon}</span>
        </Tooltip>
      );
    }
    return icon;
  };

  const iconToDisplay = generateIcon();

  return (
    <div className="u-display-flex u-alignItems-center">
      <div className="u-padding-right u-padding-top">{iconToDisplay}</div>
      <div className="u-display-flex u-flexDirection-column">
        {showSyncHealthDescription && status ? (
          <div className="u-padding-bottom">
            <div className="typo-h6 typo-bold">{MERGE_SYNC_HEALTH_STATUS_MAP[status].header}</div>
            {descriptionToDisplay ? (
              <div className="u-margin-top-xs typo-subtitle u-lineHeight-normal">
                {descriptionToDisplay} {contactSupportStatement}
              </div>
            ) : null}
            {actionButton || null}
          </div>
        ) : null}
        {children}
      </div>
    </div>
  );
};
